package com.castly.admin.ui.components

import androidx.compose.runtime.*
import com.castly.admin.ui.icons.IconPlus
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

class DropdownMenuItem(
    val label: String,
    val description: String? = null,
    val icon: (@Composable () -> Unit)? = null,
    val onClick: () -> Unit
)

@Composable
fun IconDropdownButton(
    tooltip: String,
    items: List<DropdownMenuItem>,
    icon: @Composable () -> Unit = { IconPlus(size = 18) },
    bgColor: String? = null
) {
    val colors = AppTheme.colors
    var showMenu by remember { mutableStateOf(false) }

    Div({
        style {
            property("position", "relative")
            display(DisplayStyle.InlineBlock)
        }
    }) {
        Tooltip(text = tooltip) {
            Button({
                onClick { showMenu = !showMenu }
                attr("aria-label", tooltip)
                style {
                    padding(Spacing.sm)
                    backgroundColor(Color(bgColor ?: colors.primary))
                    color(Color("#ffffff"))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    width(36.px)
                    height(36.px)
                }
            }) {
                icon()
            }
        }

        if (showMenu) {
            // Click-away overlay
            Div({
                onClick { showMenu = false }
                style {
                    property("position", "fixed")
                    property("top", "0")
                    property("left", "0")
                    width(100.vw)
                    height(100.vh)
                    property("z-index", "999")
                }
            })

            // Dropdown menu
            Div({
                style {
                    property("position", "absolute")
                    property("top", "100%")
                    property("right", "0")
                    property("z-index", "1000")
                    marginTop(Spacing.xs)
                    backgroundColor(Color(colors.surface))
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.12)")
                    minWidth(200.px)
                    overflow("hidden")
                    themeTransition()
                }
            }) {
                items.forEach { item ->
                    Div({
                        onClick {
                            item.onClick()
                            showMenu = false
                        }
                        style {
                            padding(Spacing.md, Spacing.lg)
                            cursor("pointer")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.sm)
                            property("transition", "background-color 0.15s")
                        }
                        onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
                        onMouseOut { it.target.asDynamic().style.backgroundColor = "transparent" }
                    }) {
                        val iconFn = item.icon
                        if (iconFn != null) {
                            Span({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    color(Color(colors.textMuted))
                                }
                            }) { iconFn() }
                        }
                        Div {
                            Div({
                                style {
                                    fontSize(Typography.base)
                                    fontWeight("500")
                                    color(Color(colors.text))
                                }
                            }) { Text(item.label) }
                            if (item.description != null) {
                                Div({
                                    style {
                                        fontSize(Typography.xs)
                                        color(Color(colors.textMuted))
                                        marginTop(2.px)
                                    }
                                }) { Text(item.description) }
                            }
                        }
                    }
                }
            }
        }
    }
}
